import React, { createContext, useContext, useEffect, useState } from 'react'
import { THEME_IDS, THEMES, getSystemThemePreference } from '../styles/themes'
import { applyTheme } from '../utils/themeHandler'

/**
 * Theme Context for managing application theme
 *
 * Provides theme state and switching functionality throughout the app.
 * Persists user theme preference to localStorage.
 * Supports system theme preference detection.
 */

const ThemeContext = createContext({
    currentTheme: THEME_IDS.LIGHT,
    setTheme: () => {},
    availableThemes: [],
    isSystemTheme: false,
    setSystemTheme: () => {},
})

const STORAGE_KEY = 'scoop-theme-preference'
const SYSTEM_PREFERENCE_KEY = 'scoop-use-system-theme'

export const ThemeProvider = ({ children }) => {
    // Check if user wants to use system preference
    const [isSystemTheme, setIsSystemTheme] = useState(() => {
        const stored = localStorage.getItem(SYSTEM_PREFERENCE_KEY)
        return stored === 'true'
    })

    // Get initial theme from localStorage or system preference
    const [currentTheme, setCurrentTheme] = useState(() => {
        if (isSystemTheme) {
            return getSystemThemePreference()
        }
        const stored = localStorage.getItem(STORAGE_KEY)
        return stored && THEMES[stored] ? stored : THEME_IDS.LIGHT
    })

    // Apply theme when it changes
    useEffect(() => {
        const theme = THEMES[currentTheme]
        if (theme) {
            applyTheme(theme.colors)
            // Set data-theme attribute for CSS selectors
            document.documentElement.setAttribute('data-theme', currentTheme)
        }
    }, [currentTheme])

    // Listen for system theme changes when using system preference
    useEffect(() => {
        if (!isSystemTheme) return

        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

        const handleChange = (e) => {
            setCurrentTheme(e.matches ? THEME_IDS.DARK : THEME_IDS.LIGHT)
        }

        // Modern browsers
        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', handleChange)
            return () => mediaQuery.removeEventListener('change', handleChange)
        }
        // Legacy browsers
        else if (mediaQuery.addListener) {
            mediaQuery.addListener(handleChange)
            return () => mediaQuery.removeListener(handleChange)
        }
    }, [isSystemTheme])

    /**
     * Set theme manually
     * @param {string} themeId - Theme identifier (THEME_IDS.LIGHT or THEME_IDS.DARK)
     */
    const setTheme = (themeId) => {
        if (!THEMES[themeId]) {
            console.warn(`Theme "${themeId}" not found`)
            return
        }

        setCurrentTheme(themeId)
        setIsSystemTheme(false)

        // Persist to localStorage
        localStorage.setItem(STORAGE_KEY, themeId)
        localStorage.setItem(SYSTEM_PREFERENCE_KEY, 'false')
    }

    /**
     * Enable system theme preference
     * @param {boolean} useSystem - Whether to use system preference
     */
    const setSystemTheme = (useSystem) => {
        setIsSystemTheme(useSystem)
        localStorage.setItem(SYSTEM_PREFERENCE_KEY, String(useSystem))

        if (useSystem) {
            const systemTheme = getSystemThemePreference()
            setCurrentTheme(systemTheme)
            localStorage.removeItem(STORAGE_KEY) // Remove manual preference
        }
    }

    const value = {
        currentTheme,
        setTheme,
        availableThemes: Object.values(THEMES),
        isSystemTheme,
        setSystemTheme,
    }

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

/**
 * Hook to access theme context
 * @returns {Object} Theme context value
 */
export const useTheme = () => {
    const context = useContext(ThemeContext)
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider')
    }
    return context
}
